import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { AlertCircle, Bell, X } from "lucide-react";

// ─── Animations ───────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-30px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0)     scale(1);    }
`;

const slideOut = keyframes`
  from { opacity: 1; transform: translateY(0)     scale(1);    }
  to   { opacity: 0; transform: translateY(-20px) scale(0.96); }
`;

// ─── Progress bar fill animation — duration-driven ───────────────────────────

const fillBar = (duration) => keyframes`
  from { width: 100%; }
  to   { width: 0%;   }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
`;

const ToastCard = styled.div`
  pointer-events: auto;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.04),
    0 12px 32px rgba(0, 0, 0, 0.1);
  padding: 14px 16px 18px;
  border-radius: 12px;
  min-width: 300px;
  max-width: 380px;
  animation: ${(props) => (props.$isClosing ? slideOut : slideIn)} 0.3s
    cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
`;

// Left accent bar — coloured per variant
const AccentBar = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: ${(props) =>
    props.$variant === "push"
      ? "linear-gradient(180deg, #4f46e5, #7c3aed)"
      : "var(--primary, #be6f72)"};
  border-radius: 12px 0 0 12px;
`;

// Progress countdown bar at the bottom
const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: ${(props) =>
    props.$variant === "push"
      ? "linear-gradient(90deg, #4f46e5, #7c3aed)"
      : "var(--primary, #be6f72)"};
  opacity: 0.35;
  animation: ${(props) => fillBar(props.$duration)}
    ${(props) => props.$duration}ms linear forwards;
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.$variant === "push"
      ? "linear-gradient(135deg, #eef2ff, #ede9fe)"
      : "rgba(190, 111, 114, 0.1)"};
  margin-left: 4px;
  margin-top: 1px;
  flex-shrink: 0;
`;

const PushIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
`;

const TextBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.p`
  font-family: "montserrat-semibold", "Inter", sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Body = styled.p`
  font-family: "montserrat-regular", "Inter", sans-serif;
  font-size: 0.8rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.45;
  /* Allow up to 3 lines */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* Plain message (non-push variant) */
const Message = styled.span`
  font-family: "montserrat-regular", "Inter", sans-serif;
  font-size: 0.88rem;
  color: #333;
  flex-grow: 1;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: color 0.15s ease;
  margin-top: 1px;

  &:hover {
    color: #374151;
  }
`;

// ─── Single Toast ─────────────────────────────────────────────────────────────

function ToastItem({ id, message, title, body, icon, variant, duration, onClose }) {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(handleClose, duration);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onClose(id), 320);
  };

  return (
    <ToastCard $isClosing={isClosing}>
      <AccentBar $variant={variant} />

      {/* Icon */}
      <IconWrapper $variant={variant}>
        {variant === "push" && icon ? (
          <PushIcon src={icon} alt="" />
        ) : variant === "push" ? (
          <Bell size={18} color="#4f46e5" />
        ) : (
          <AlertCircle size={18} color="var(--primary, #be6f72)" />
        )}
      </IconWrapper>

      {/* Content */}
      {variant === "push" ? (
        <TextBlock>
          {title && <Title>{title}</Title>}
          {body  && <Body>{body}</Body>}
        </TextBlock>
      ) : (
        <Message>{message}</Message>
      )}

      <CloseButton onClick={handleClose} aria-label="Close notification">
        <X size={15} />
      </CloseButton>

      {/* Auto-dismiss progress bar */}
      <ProgressBar $variant={variant} $duration={duration} />
    </ToastCard>
  );
}

// ─── Toast Manager (supports a queue of toasts) ───────────────────────────────

/**
 * <ToastManager />
 *
 * Drop this once anywhere in your app (App.js).
 * It:
 *   1. Renders any `push-toast` CustomEvent as a push-style toast.
 *   2. Optionally exposes `message` / `onClose` props for the old
 *      auto-logout style toast (backwards compatible).
 */
export function ToastManager({ message, onClose }) {
  const [toasts, setToasts] = useState([]);

  // ── Handle legacy single-message prop (auto-logout etc.) ─────────────────
  useEffect(() => {
    if (!message) return;
    addToast({ message, variant: "alert", duration: 5000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  // ── Listen for foreground push notifications ─────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const { title, body, icon } = e.detail || {};
      addToast({ title, body, icon, variant: "push", duration: 7000 });
    };
    window.addEventListener("push-toast", handler);
    return () => window.removeEventListener("push-toast", handler);
  }, []);

  const addToast = (opts) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, ...opts }]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    // Call legacy onClose if there are no more toasts and prop is set
    if (onClose) onClose();
  };

  if (!toasts.length) return null;

  return (
    <ToastContainer>
      {toasts.map((t) => (
        <ToastItem key={t.id} {...t} onClose={removeToast} />
      ))}
    </ToastContainer>
  );
}

// ─── Legacy default export (kept for backward compatibility) ──────────────────

export default function Toast({ message, duration = 5000, onClose }) {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(handleClose, duration);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { if (onClose) onClose(); }, 300);
  };

  return (
    <ToastContainer>
      <ToastCard $isClosing={isClosing}>
        <AccentBar />
        <IconWrapper>
          <AlertCircle size={18} color="var(--primary, #be6f72)" />
        </IconWrapper>
        <Message>{message}</Message>
        <CloseButton onClick={handleClose} aria-label="Close">
          <X size={15} />
        </CloseButton>
        <ProgressBar $duration={duration} />
      </ToastCard>
    </ToastContainer>
  );
}
